import { useState } from "react";
import { X, ScrollText, Check, ChevronDown } from "lucide-react";
import { TERMS_BODY, TERMS_VERSION } from "@/lib/terms";
import { renderMarkdownBody } from "@/lib/simpleMarkdown";
import { useScrollCue } from "@/hooks/useScrollCue";

/**
 * Terms of Use modal — admin must scroll, tick the checkbox, then click
 * "Accept & Continue". Used in three places:
 *   1. Inside the Landing → Request League form (gate the submit)
 *   2. As a blocking overlay on AdminPanel for existing admins who haven't
 *      accepted yet (backfill scenario)
 *   3. Pure viewing (`viewOnly`) from marketing footers — no checkbox/
 *      accept gate, just a Close button (nothing to "accept" there,
 *      the real gate happens at actual sign-up).
 *
 * The parent owns whether the modal is open and what happens on accept —
 * this component is presentational + state for the checkbox/scroll only.
 *
 * iter87.199.145 — Body now renders via the shared `renderMarkdownBody`
 * (also used by `/terms`, `/privacy`, `/terms/pro-plus-billing`) instead
 * of a bespoke line-by-line renderer that fragmented hard-wrapped
 * bullets/paragraphs across separate lines in the modal.
 * iter87.199.146 — Added `viewOnly` mode + a "more below" scroll cue.
 */
export default function TermsModal({
    open,
    onAccept,
    onClose, // optional — pass null when the modal is blocking (no dismiss)
    busy = false,
    title = "Terms of Use",
    onShowPrivacy = null, // optional — when provided, shows a "View Privacy Notice" cross-link in the footer
    viewOnly = false, // iter87.199.146 — hide the checkbox/accept gate, just Close
}) {
    const [checked, setChecked] = useState(false);
    const [bodyRef, showScrollCue] = useScrollCue([open]);

    if (!open) return null;

    const accept = () => {
        if (!checked || busy) return;
        onAccept?.(TERMS_VERSION);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            data-testid="terms-modal-backdrop"
            // No backdrop click-to-close when blocking
            onClick={(e) => { if (onClose && e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="brutal-card max-w-2xl w-full max-h-[85dvh] flex flex-col"
                data-testid="terms-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 p-5 border-b-2 border-[#222]">
                    <div className="flex items-center gap-2 min-w-0">
                        <ScrollText className="w-5 h-5 text-cyber shrink-0" />
                        <h2 className="font-display font-black text-xl uppercase truncate">{title}</h2>
                    </div>
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="brutal-btn-ghost p-1.5"
                            data-testid="terms-modal-close-btn"
                            aria-label="Close terms"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
                <div
                    ref={bodyRef}
                    className="overflow-y-auto px-5 py-3 flex-1 space-y-2"
                    data-testid="terms-modal-body"
                >
                    {renderMarkdownBody(TERMS_BODY)}
                    {showScrollCue && (
                        <div
                            className="sticky bottom-0 -mx-5 -mb-3 h-10 bg-gradient-to-t from-[#121212] to-transparent flex items-end justify-center pb-1 pointer-events-none"
                            data-testid="terms-modal-scroll-cue"
                        >
                            <ChevronDown className="w-4 h-4 text-white/50 animate-bounce" />
                        </div>
                    )}
                </div>
                {viewOnly ? (
                    <div className="p-3 border-t-2 border-[#222] bg-ink flex items-center justify-between">
                        <p className="text-[10px] text-zinc-600 font-mono">Informational — no acceptance required.</p>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="terms-modal-close-footer-btn"
                            className="brutal-btn-ghost text-xs"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <div className="p-5 border-t-2 border-[#222] space-y-3 bg-ink">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => setChecked(e.target.checked)}
                                data-testid="terms-modal-checkbox"
                                className="mt-0.5 accent-cyber w-4 h-4"
                            />
                            <span className="text-xs text-zinc-300 leading-relaxed">
                                I've read these terms and agree that I'm using Tiny Crew Rivalries for <strong className="text-white">entertainment purposes only</strong>. I accept the disclaimer above and agree to hold Tiny Crew Rivalries harmless.
                            </span>
                        </label>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-3 flex-wrap">
                                <p className="text-[10px] text-zinc-600 font-mono">Version {TERMS_VERSION}</p>
                                {onShowPrivacy && (
                                    <button
                                        type="button"
                                        data-testid="terms-modal-view-privacy-btn"
                                        onClick={onShowPrivacy}
                                        className="text-[10px] text-cyber underline-offset-2 hover:underline"
                                    >
                                        View Privacy Notice →
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                data-testid="terms-modal-accept-btn"
                                onClick={accept}
                                disabled={!checked || busy}
                                className="brutal-btn-primary text-sm py-2 disabled:opacity-30"
                            >
                                <Check className="w-4 h-4" /> {busy ? "Saving..." : "Accept & Continue"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
